import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  RANGE_PRESET,
  THERAPY_PREP_FILTERS,
  buildDateSections,
  endOfDay,
  formatPeriodLabel,
  getLoggedByName,
  getMonthRange,
  getTherapyPrepCategory,
  getWeekRange,
  isValidDate,
  startOfDay,
  toEntryDate,
} from "./reportBuilders/dailyCareReportEntryUtils";
import {
  buildKeyEvents,
  buildQuestionsToDiscuss,
  buildTherapyPrepSummary,
} from "./reportBuilders/dailyCareReportBuilder";
import { functions } from "../../services/firebase";

const generateTherapyPrepSummaryCallable = httpsCallable(functions, "generateTherapyPrepSummary");
const MIN_SUMMARY_LOADING_MS = 700;

const summarizeEntriesForAi = (entries = []) => entries.map((entry) => ({
  id: entry?.id || "",
  category: entry?.category || entry?.type || "note",
  title: entry?.title || "",
  note: entry?.note || entry?.summary || entry?.text || "",
  date: (() => {
    const date = toEntryDate(entry?.timestamp || entry?.date || null);
    return isValidDate(date) ? date.toISOString() : null;
  })(),
  time: entry?.localTime || entry?.time || null,
  metadata: entry?.metadata && typeof entry.metadata === "object"
    ? entry.metadata
    : {},
}));

export const useDailyCareReportControls = ({ entries = [] }) => {
  const [rangePreset, setRangePreset] = useState(RANGE_PRESET.WEEK);
  const [customStartDate, setCustomStartDate] = useState(() => getWeekRange().start);
  const [customEndDate, setCustomEndDate] = useState(() => getWeekRange().end);
  const [selectedFilters, setSelectedFilters] = useState(() => THERAPY_PREP_FILTERS.map((item) => item.key));
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(() => buildTherapyPrepSummary([]));
  const [summaryLoading, setSummaryLoading] = useState(false);

  const activeRange = useMemo(() => {
    if (rangePreset === RANGE_PRESET.MONTH) return getMonthRange();
    if (rangePreset === RANGE_PRESET.CUSTOM) return { start: customStartDate, end: customEndDate };
    return getWeekRange();
  }, [customEndDate, customStartDate, rangePreset]);

  const rangeStart = useMemo(() => startOfDay(activeRange.start), [activeRange]);
  const rangeEnd = useMemo(() => endOfDay(activeRange.end), [activeRange]);

  const filteredEntries = useMemo(() => {
    return entries
      .filter((entry) => {
        const date = toEntryDate(entry.timestamp);
        return isValidDate(date) && date >= rangeStart && date <= rangeEnd;
      })
      .filter((entry) => selectedFilters.includes(getTherapyPrepCategory(entry)));
  }, [entries, rangeEnd, rangeStart, selectedFilters]);

  const dateSections = useMemo(() => buildDateSections(filteredEntries), [filteredEntries]);
  const keyEvents = useMemo(() => buildKeyEvents(filteredEntries), [filteredEntries]);
  const questionsToDiscuss = useMemo(() => buildQuestionsToDiscuss(summaryData), [summaryData]);
  const peopleLoggedCount = useMemo(() => {
    return new Set(filteredEntries.map(getLoggedByName).filter(Boolean)).size;
  }, [filteredEntries]);
  const periodLabel = useMemo(
    () => formatPeriodLabel(rangeStart, rangeEnd, rangePreset),
    [rangeEnd, rangePreset, rangeStart]
  );
  const isCustomRangeInvalid = !isValidDate(rangeStart) || !isValidDate(rangeEnd) || rangeStart > rangeEnd;
  const reportChildName = useMemo(() => {
    const firstNamedEntry = entries.find((entry) => typeof entry?.childName === "string" && entry.childName.trim());
    return firstNamedEntry?.childName?.trim() || "";
  }, [entries]);

  useEffect(() => {
    const fallbackSummary = buildTherapyPrepSummary(filteredEntries);
    setSummaryData(fallbackSummary);

    if (!filteredEntries.length) {
      setSummaryLoading(false);
      return undefined;
    }

    let isActive = true;

    const loadAiSummary = async () => {
      const startedAt = Date.now();
      setSummaryLoading(true);
      try {
        const response = await generateTherapyPrepSummaryCallable({
          childName: reportChildName,
          periodLabel,
          entries: summarizeEntriesForAi(filteredEntries),
        });

        const aiSummary = response?.data && typeof response.data === "object" ? response.data : null;
        const normalizedSummary = aiSummary ? {
          weeklySummary: typeof aiSummary.weeklySummary === "string" && aiSummary.weeklySummary.trim()
            ? aiSummary.weeklySummary.trim()
            : fallbackSummary.weeklySummary,
          bullets: Array.isArray(aiSummary.bullets)
            ? aiSummary.bullets
                .map((bullet) => ({
                  label: typeof bullet?.label === "string" ? bullet.label.trim() : "",
                  text: typeof bullet?.text === "string" ? bullet.text.trim() : "",
                }))
                .filter((bullet) => bullet.label && bullet.text)
            : fallbackSummary.bullets,
          notableConcerns: Array.isArray(aiSummary.notableConcerns)
            ? aiSummary.notableConcerns.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim())
            : fallbackSummary.notableConcerns,
          possiblePatterns: Array.isArray(aiSummary.possiblePatterns)
            ? aiSummary.possiblePatterns.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim())
            : fallbackSummary.possiblePatterns,
          questionsToDiscuss: Array.isArray(aiSummary.questionsToDiscuss)
            ? aiSummary.questionsToDiscuss
                .map((item) => {
                  if (typeof item === "string") {
                    const question = item.trim();
                    return question ? { question, why: "", sourceEntryIds: [] } : null;
                  }

                  const question = typeof item?.question === "string" ? item.question.trim() : "";
                  if (!question) {
                    return null;
                  }

                  return {
                    question,
                    why: typeof item?.why === "string" ? item.why.trim() : "",
                    sourceEntryIds: Array.isArray(item?.sourceEntryIds)
                      ? item.sourceEntryIds.filter((id) => typeof id === "string" && id.trim()).map((id) => id.trim())
                      : [],
                  };
                })
                .filter(Boolean)
                .slice(0, 5)
            : fallbackSummary.questionsToDiscuss,
        } : fallbackSummary;

        if (isActive) {
          setSummaryData(normalizedSummary);
        }
      } catch (error) {
        console.error("Error generating AI care team update summary:", error);
        if (isActive) {
          setSummaryData(fallbackSummary);
        }
      } finally {
        const elapsed = Date.now() - startedAt;
        const remainingDelay = Math.max(0, MIN_SUMMARY_LOADING_MS - elapsed);

        if (remainingDelay > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, remainingDelay));
        }

        if (isActive) {
          setSummaryLoading(false);
        }
      }
    };

    loadAiSummary();

    return () => {
      isActive = false;
    };
  }, [filteredEntries, periodLabel, reportChildName]);

  const toggleFilter = (filterKey) => {
    setSelectedFilters((current) => {
      if (current.includes(filterKey)) {
        return current.filter((item) => item !== filterKey);
      }
      return [...current, filterKey];
    });
  };

  const clearFilters = () => setSelectedFilters([]);
  const selectAllFilters = () => setSelectedFilters(THERAPY_PREP_FILTERS.map((item) => item.key));

  return {
    rangePreset,
    setRangePreset,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    selectedFilters,
    setSelectedFilters,
    filterPanelOpen,
    setFilterPanelOpen,
    rangeStart,
    rangeEnd,
    filteredEntries,
    dateSections,
    summaryData,
    summaryLoading,
    keyEvents,
    questionsToDiscuss,
    peopleLoggedCount,
    periodLabel,
    isCustomRangeInvalid,
    toggleFilter,
    clearFilters,
    selectAllFilters,
  };
};
